import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    FileCheck,
    Loader2,
    Trash2,
    Plus,
    Check,
    Edit3,
    Calculator,
    Lock,
    Unlock,
    ShieldCheck,
    Ruler,
    FileText,
    Receipt,
    MessageSquare,
    RotateCcw,
    Sparkles,
    X,
} from "lucide-react";
import TopNav from "../components/TopNav";
import {
    listAllEstimates,
    updateEstimate,
    updateEstimateGuarded,
    deleteEstimate,
    lockEstimate,
    unlockEstimate,
    getProject,
    reviseEstimate,
    createEstimate,
    fillEstimateDescriptions,
} from "../lib/api";
import InlineEditText from "../components/InlineEditText";
import CostProfitPopup from "../components/CostProfitPopup";
import QuoteView from "../components/QuoteView";
import ConcurrentSaveDialog from "../components/ConcurrentSaveDialog";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

const fmt = (n) =>
    (Number(n) || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const UNITS = [
    "ea", "each", "lf", "ft", "in", "yd", "mi", "mm", "cm", "m", "km",
    "sqft", "sqyd", "sqm", "cuft", "cuyd", "cum", "lump sum", "hr", "day", "",
];

const PRICE_TYPES = [
    { value: "fixed", label: "Fixed" },
    { value: "per_item", label: "Per item" },
    { value: "per_length", label: "Per unit (length/area/volume/weight)" },
];

const mdComponents = {
    h1: (p) => <h1 className="text-xl font-bold text-white mt-5 mb-2" {...p} />,
    h2: (p) => <h2 className="text-lg font-bold text-white mt-5 mb-2 pl-2 border-l-2 border-[#00A3FF]" {...p} />,
    h3: (p) => <h3 className="text-sm font-bold text-white mt-4 mb-1.5 uppercase tracking-wide" {...p} />,
    p: (p) => <p className="text-sm text-slate-200 leading-relaxed my-2" {...p} />,
    ul: (p) => <ul className="list-disc ml-5 my-2 space-y-1 text-sm text-slate-200" {...p} />,
    ol: (p) => <ol className="list-decimal ml-5 my-2 space-y-1 text-sm text-slate-200" {...p} />,
    li: (p) => <li className="leading-relaxed" {...p} />,
    strong: (p) => <strong className="text-white font-bold" {...p} />,
    table: (p) => (
        <div className="my-3 overflow-x-auto rounded-sm border border-[#2A3655]">
            <table className="w-full text-xs border-collapse" {...p} />
        </div>
    ),
    thead: (p) => <thead className="bg-[#0A1128] text-[10px] uppercase tracking-wider text-slate-400 font-bold" {...p} />,
    tr: (p) => <tr className="border-b border-[#1C253C] last:border-0" {...p} />,
    th: (p) => <th className="text-left px-2 py-1.5 font-bold" {...p} />,
    td: (p) => <td className="px-2 py-1.5 text-slate-200 align-top" {...p} />,
    a: (p) => <a className="text-[#00A3FF] underline" target="_blank" rel="noopener noreferrer" {...p} />,
};

export default function ReportsPage() {
    const navigate = useNavigate();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeId, setActiveId] = useState(null);

    // iter-108: deep-link support. The Notifications dropdown links to
    // ?project=<id>&view=quote so clicking "New quote auto-generated"
    // jumps straight to that project's quote tab.
    const initialParams = useMemo(() => {
        if (typeof window === "undefined") return { project: null, view: null };
        const p = new URLSearchParams(window.location.search);
        return { project: p.get("project"), view: p.get("view") };
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await listAllEstimates();
            setReports(data);
            if (!activeId && data.length > 0) {
                // Prefer the deep-linked project's latest estimate.
                if (initialParams.project) {
                    const match = data.find((r) => r.project_id === initialParams.project);
                    if (match) {
                        setActiveId(match.id);
                        return;
                    }
                }
                setActiveId(data[0].id);
            }
        } catch (e) {
            toast.error("Couldn't load reports");
        } finally {
            setLoading(false);
        }
    }, [activeId, initialParams.project]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const active = reports.find((r) => r.id === activeId) || null;

    // iter-137 — concurrent-save conflict state, cleared once the user
    // picks overwrite / discard / cancel.
    const [conflict, setConflict] = useState(null);

    const updateActive = (patch) => {
        setReports((prev) =>
            prev.map((r) => (r.id === activeId ? { ...r, ...patch } : r)),
        );
    };

    const save = async (patch) => {
        if (!active) return;
        if (active.locked) {
            toast.error(`Bid is locked (v${active.revision}). Unlock to edit.`);
            throw new Error("locked");
        }
        try {
            const updated = await updateEstimateGuarded(
                active.id,
                patch,
                typeof active.saves === "number" ? active.saves : undefined,
            );
            updateActive(updated);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const isConflict = err?.response?.status === 409 && detail?.code === "concurrent_save";
            if (isConflict) {
                if (typeof detail.current_saves === "number") {
                    updateActive({ saves: detail.current_saves });
                }
                setConflict({ detail, patch });
                // Swallow — the dialog owns the retry UX from here.
                return;
            }
            toast.error("Couldn't save");
            throw err;
        }
    };

    const handleOverwriteAfterConflict = async () => {
        if (!conflict || !active) return;
        const patch = conflict.patch;
        setConflict(null);
        try {
            const updated = await updateEstimateGuarded(
                active.id,
                patch,
                typeof active.saves === "number" ? active.saves : undefined,
            );
            updateActive(updated);
            toast.success("Your version saved on top");
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Couldn't overwrite");
        }
    };
    const handleDiscardAfterConflict = () => {
        setConflict(null);
        toast.message("Kept the other version. Your edits were discarded.");
    };

    const handleDelete = async () => {
        if (!active) return;
        if (!window.confirm(`Delete "${active.label}"? This cannot be undone.`)) return;
        try {
            await deleteEstimate(active.id);
            toast.success("Report deleted");
            const remaining = reports.filter((r) => r.id !== active.id);
            setReports(remaining);
            setActiveId(remaining[0]?.id || null);
        } catch {
            toast.error("Couldn't delete");
        }
    };

    const handleToggleLock = async () => {
        if (!active) return;
        try {
            const updated = active.locked
                ? await unlockEstimate(active.id)
                : await lockEstimate(active.id);
            updateActive(updated);
            toast.success(
                updated.locked
                    ? `Bid locked as v${updated.revision}`
                    : "Bid unlocked — editing enabled",
            );
        } catch (err) {
            toast.error("Couldn't toggle lock");
        }
    };

    // iter-110: retro-fill descriptions on an existing estimate.
    // Single cheap LLM call — no full re-price. Great for old
    // estimates whose Description column came back empty.
    const [fillingDescriptions, setFillingDescriptions] = useState(false);
    const handleFillDescriptions = async () => {
        if (!active || fillingDescriptions) return;
        if (active.locked) {
            toast.error("Unlock this bid first");
            return;
        }
        setFillingDescriptions(true);
        try {
            const updated = await fillEstimateDescriptions(active.id);
            updateActive(updated);
            toast.success("Descriptions filled");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't fill descriptions",
            );
        } finally {
            setFillingDescriptions(false);
        }
    };

    // iter-110: Retry with a different pricing strategy. Creates a
    // sibling estimate so the user can A/B compare Cost / Competitive
    // / Average without losing the current one. Prepended to the list
    // and auto-selected.
    const [retryingStrategy, setRetryingStrategy] = useState(null);
    const [retryStage, setRetryStage] = useState("");
    const currentStrategy = (active?.pricing_strategy || "average").toLowerCase();
    const strategyOptions = [
        { id: "cost", label: "Cost", blurb: "Straight cost — no markup, floor pricing" },
        { id: "competitive", label: "Competitive", blurb: "~10-20% below median — win-bid aggressive" },
        { id: "average", label: "Average", blurb: "Regional median — realistic market rate" },
    ];
    const handleRetryStrategy = async (strategy) => {
        if (!active || retryingStrategy) return;
        setRetryingStrategy(strategy);
        setRetryStage("Queuing new estimate…");
        try {
            const payload = {
                project_id: active.project_id,
                template_id: active.template_id || null,
                template_ids: active.template_ids || null,
                all_templates: !!active.all_templates,
                document_ids: active.document_ids || null,
                extra_notes: active.extra_notes || "",
                scope: active.scope || "overall",
                trade: active.trade || null,
                trades: active.trades || null,
                plugin_ids: active.plugin_ids || null,
                plugin_prompt_ids: active.plugin_prompt_ids || null,
                plugin_adhoc_prompts: active.plugin_adhoc_prompts || null,
                plugin_prompt_inputs: active.plugin_prompt_inputs || null,
                profit_margin_pct: active.profit_margin_pct || 0,
                hide_profit_in_lines: !!active.hide_profit_in_lines,
                pricing_strategy: strategy,
                takeoff_items: active.takeoff_items || null,
            };
            const created = await createEstimate(payload, (s) => {
                setRetryStage(s?.stage || "Working…");
            });
            toast.success(
                `Re-priced with ${strategy.charAt(0).toUpperCase() + strategy.slice(1)} strategy`,
            );
            setReports((prev) => [created, ...prev]);
            setActiveId(created.id);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || err?.message || "Retry failed",
            );
        } finally {
            setRetryingStrategy(null);
            setRetryStage("");
        }
    };

    // Group reports by project for the dropdown
    const grouped = useMemo(() => {
        const byProj = {};
        reports.forEach((r) => {
            const key = r.project_name || "Untitled project";
            (byProj[key] = byProj[key] || []).push(r);
        });
        return byProj;
    }, [reports]);

    return (
        <div className="h-[100dvh] overflow-hidden blueprint-bg text-slate-100 flex flex-col" data-testid="reports-page">
            <TopNav active="reports" />

            <div className="shrink-0 border-b border-[#2A3655] bg-[#0A1128]/80">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-5">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-1">
                        <FileCheck className="w-3.5 h-3.5 text-[#00A3FF]" />
                        Estimate reports
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white mb-1">
                        Reports
                    </h1>
                    <p className="text-slate-400 text-sm mb-4 max-w-2xl">
                        Every estimate you save shows up here. Pick a report below to view,
                        edit, or export it.
                    </p>

                    {/* Report picker */}
                    <div className="flex items-center gap-2">
                        <Select value={activeId || ""} onValueChange={(v) => setActiveId(v)}>
                            <SelectTrigger
                                className="bg-[#0A1128] border-[#2A3655] text-white h-11 max-w-md flex-1"
                                data-testid="reports-select-trigger"
                            >
                                <SelectValue placeholder={loading ? "Loading…" : "Choose a report"} />
                            </SelectTrigger>
                            <SelectContent
                                className="bg-[#0F1B3A] border-[#2A3655] text-white max-h-[70vh]"
                                data-testid="reports-select-content"
                            >
                                {Object.keys(grouped).length === 0 && (
                                    <div className="text-xs text-slate-500 px-3 py-3 italic">
                                        No reports saved yet.
                                    </div>
                                )}
                                {Object.entries(grouped).map(([projName, list]) => (
                                    <div key={projName}>
                                        <div className="text-[10px] uppercase tracking-wider text-[#00A3FF] font-bold px-2 pt-2 pb-1 border-b border-[#1C253C]">
                                            {projName}
                                        </div>
                                        {list.map((r) => (
                                            <SelectItem
                                                key={r.id}
                                                value={r.id}
                                                className="focus:bg-[#1C253C] focus:text-white py-2"
                                                data-testid={`reports-option-${r.id}`}
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Calculator className="w-3.5 h-3.5 text-[#00A3FF] shrink-0" />
                                                    <div className="min-w-0">
                                                        <div className="text-sm truncate">{r.label}</div>
                                                        <div className="text-[10px] text-slate-500 tabular-nums">
                                                            ${fmt(r.totals?.grand_total)} · {(r.lines || []).length} lines
                                                        </div>
                                                    </div>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </div>
                                ))}
                            </SelectContent>
                        </Select>
                        {active && (
                            <>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            type="button"
                                            disabled={!!retryingStrategy || active.locked}
                                            data-testid="reports-retry-strategy-btn"
                                            title={
                                                active.locked
                                                    ? "Unlock this bid to re-price"
                                                    : "Re-run this estimate with a different pricing strategy"
                                            }
                                            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider bg-[#1C253C] border border-[#2A3655] text-slate-300 hover:border-[#00A3FF] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {retryingStrategy ? (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#00A3FF]" />
                                            ) : (
                                                <Sparkles className="w-3.5 h-3.5 text-[#00A3FF]" />
                                            )}
                                            <span className="hidden sm:inline">
                                                {retryingStrategy ? "Re-pricing…" : "Re-price"}
                                            </span>
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        align="end"
                                        className="bg-[#131A2F] border-[#2A3655] text-white w-[280px]"
                                        data-testid="reports-retry-strategy-menu"
                                    >
                                        <DropdownMenuLabel className="text-slate-400 text-[10px] uppercase tracking-widest">
                                            Re-run with pricing strategy
                                        </DropdownMenuLabel>
                                        <DropdownMenuSeparator className="bg-[#2A3655]" />
                                        {strategyOptions.map((opt) => {
                                            const isCurrent = opt.id === currentStrategy;
                                            return (
                                                <DropdownMenuItem
                                                    key={opt.id}
                                                    disabled={isCurrent || !!retryingStrategy}
                                                    onSelect={(e) => {
                                                        e.preventDefault();
                                                        handleRetryStrategy(opt.id);
                                                    }}
                                                    data-testid={`reports-retry-strategy-${opt.id}`}
                                                    className="flex-col items-start gap-0.5 hover:bg-[#1C253C] focus:bg-[#1C253C] cursor-pointer data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed"
                                                >
                                                    <div className="flex items-center gap-2 w-full">
                                                        <span className="font-semibold text-sm">
                                                            {opt.label}
                                                        </span>
                                                        {isCurrent && (
                                                            <span className="ml-auto text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#00A3FF]/20 text-[#00A3FF] font-bold">
                                                                Current
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-[11px] text-slate-400 leading-tight">
                                                        {opt.blurb}
                                                    </span>
                                                </DropdownMenuItem>
                                            );
                                        })}
                                        {retryStage && (
                                            <>
                                                <DropdownMenuSeparator className="bg-[#2A3655]" />
                                                <div className="px-2 py-1.5 text-[11px] text-emerald-300 leading-tight">
                                                    {retryStage}
                                                </div>
                                            </>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <button
                                    type="button"
                                    onClick={handleFillDescriptions}
                                    disabled={fillingDescriptions || active.locked}
                                    data-testid="reports-fill-descriptions-btn"
                                    title={
                                        active.locked
                                            ? "Unlock this bid to fill descriptions"
                                            : "Auto-fill the Description column on every line using AI"
                                    }
                                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider bg-[#1C253C] border border-[#2A3655] text-slate-300 hover:border-emerald-400 hover:text-emerald-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {fillingDescriptions ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-400" />
                                    ) : (
                                        <Edit3 className="w-3.5 h-3.5 text-emerald-400" />
                                    )}
                                    <span className="hidden sm:inline">
                                        {fillingDescriptions ? "Filling…" : "Fill descriptions"}
                                    </span>
                                </button>
                                <button
                                    onClick={handleToggleLock}
                                    data-testid="reports-lock-btn"
                                    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors ${
                                        active.locked
                                            ? "bg-[#00A3FF]/15 border border-[#00A3FF]/40 text-[#00A3FF] hover:bg-[#00A3FF]/25"
                                            : "bg-[#1C253C] border border-[#2A3655] text-slate-300 hover:border-[#00A3FF] hover:text-white"
                                    }`}
                                    title={active.locked ? `Unlock v${active.revision}` : "Lock this bid"}
                                >
                                    {active.locked ? (
                                        <>
                                            <Lock className="w-3.5 h-3.5" />
                                            <span className="hidden sm:inline" data-testid="reports-lock-badge">
                                                Locked · v{active.revision}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <Unlock className="w-3.5 h-3.5" />
                                            <span className="hidden sm:inline">Lock bid</span>
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={handleDelete}
                                    data-testid="reports-delete-btn"
                                    className="shrink-0 p-2.5 rounded-sm text-slate-300 hover:text-red-300 hover:bg-red-500/20 transition-colors"
                                    aria-label="Delete report"
                                    title="Delete report"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <main className="flex-1 min-h-0 overflow-y-auto" data-testid="reports-body">
                {loading ? (
                    <Loader />
                ) : !active ? (
                    <EmptyState />
                ) : (
                    <ReportEditor
                        report={active}
                        onSave={save}
                        onLocalUpdate={updateActive}
                        onRevised={async (newEstimate) => {
                            // iter-98 — refetch the full list so the
                            // new revised estimate appears in the
                            // dropdown, then jump straight to it.
                            await load();
                            if (newEstimate?.id) setActiveId(newEstimate.id);
                        }}
                    />
                )}
            </main>
            {/* iter-137 — concurrent-save conflict resolver overlay. */}
            <ConcurrentSaveDialog
                open={!!conflict}
                onOpenChange={(v) => !v && setConflict(null)}
                detail={conflict?.detail}
                localPatch={conflict?.patch}
                serverEstimate={active}
                onOverwrite={handleOverwriteAfterConflict}
                onDiscard={handleDiscardAfterConflict}
            />
        </div>
    );
}

function Loader() {
    return (
        <div className="flex items-center justify-center py-20 text-slate-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading reports…
        </div>
    );
}

function EmptyState() {
    return (
        <div className="max-w-2xl mx-auto text-center py-20 px-6" data-testid="reports-empty-state">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00A3FF]/10 border border-[#00A3FF]/30 mb-4">
                <FileCheck className="w-7 h-7 text-[#00A3FF]" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">No reports yet</h2>
            <p className="text-slate-400 text-sm">
                Generate an estimate from any project's workspace and it'll show up here,
                grouped by project name.
            </p>
        </div>
    );
}

function ReportEditor({ report, onSave, onLocalUpdate, onRevised }) {
    // iter-98: merged Priced Lines + Full Report into a single tab.
    // Tab switcher now toggles between "report" (table + markdown +
    // questions) and "quote" (customer-facing quote).
    // iter-108: honor ?view=quote deep-link from email notifications.
    const initialView = (() => {
        try {
            const p = new URLSearchParams(window.location.search);
            return p.get("view") === "quote" ? "quote" : "report";
        } catch { return "report"; }
    })();
    const [view, setView] = useState(initialView); // "report" | "quote"
    const [project, setProject] = useState(null);
    const [revising, setRevising] = useState(false);
    const [reviseStage, setReviseStage] = useState(null);

    // Fetch the parent project once per active report so QuoteView has
    // access to `job_id` for the Quote ID field. Non-fatal if it fails —
    // QuoteView falls back to a truncated estimate id.
    useEffect(() => {
        let cancelled = false;
        if (!report?.project_id) {
            setProject(null);
            return () => {
                cancelled = true;
            };
        }
        (async () => {
            try {
                const p = await getProject(report.project_id);
                if (!cancelled) setProject(p);
            } catch {
                if (!cancelled) setProject(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [report?.project_id]);

    // Local handler for the floating Cost-vs-Profit popup slider. Updates
    // the record's profit_margin_pct + recomputes totals so the totals
    // strip and PDF exports reflect the new margin instantly.
    const handleMarginChange = ({ profit_margin_pct, profit_amount, final_total }) => {
        const newTotals = { ...(report.totals || {}) };
        newTotals.grand_total = final_total;
        if (!report.hide_profit_in_lines) {
            newTotals.profit_margin_line = profit_amount;
            newTotals.profit_margin_pct = profit_margin_pct;
        } else {
            delete newTotals.profit_margin_line;
            delete newTotals.profit_margin_pct;
        }
        onLocalUpdate({ totals: newTotals, profit_margin_pct, profit_amount });
        onSave({ profit_margin_pct, profit_amount, totals: newTotals });
    };

    const hasReportMd = !!(report.report && report.report.trim());

    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 pb-24">
            {!report.locked && view !== "quote" && (
                <CostProfitPopup
                    report={report}
                    onChangeMargin={handleMarginChange}
                />
            )}
            {report.locked && (
                <div
                    className="mb-5 flex items-start gap-3 rounded-sm border border-[#00A3FF]/40 bg-[#00A3FF]/10 px-4 py-3"
                    data-testid="reports-lock-banner"
                >
                    <ShieldCheck className="w-5 h-5 text-[#00A3FF] shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <div className="font-bold text-white">
                            Bid locked · Revision v{report.revision}
                        </div>
                        <div className="text-[11px] text-slate-300 mt-0.5">
                            Priced lines, totals and report text are frozen{
                                report.locked_at
                                    ? ` (locked ${new Date(report.locked_at).toLocaleString()})`
                                    : ""
                            }. Click <span className="text-[#00A3FF] font-bold">Unlock</span> above to resume editing — a new revision will be stamped next time you lock.
                        </div>
                    </div>
                </div>
            )}
            {/* Header */}
            <div className="mb-6">
                <InlineEditText
                    value={report.label}
                    onSave={(v) => onSave({ label: v })}
                    placeholder="Report label"
                    className="text-2xl md:text-3xl font-bold text-white"
                    dataTestId="report-label"
                />
                <div className="mt-1 text-[11px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-2 flex-wrap">
                    <span>{report.project_name || "Untitled project"}</span>
                    <span>·</span>
                    <span className="text-[#00A3FF]">
                        {report.scope === "trade" && report.trade
                            ? report.trade.replace(/_/g, " ")
                            : "Overall"}
                    </span>
                    <span>·</span>
                    <span>{report.template_name}</span>
                    <span>·</span>
                    <span className="tabular-nums">{new Date(report.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-2">
                    <InlineEditText
                        value={report.summary || ""}
                        onSave={(v) => onSave({ summary: v })}
                        placeholder="Add a one-line summary…"
                        className="text-sm text-slate-300"
                        dataTestId="report-summary"
                        multiline
                    />
                </div>
            </div>

            {/* iter-98: 2-tab switcher — Full report (combined priced
                lines + markdown + questions) and customer-facing Quote. */}
            <div
                className="mb-4 inline-flex rounded-sm border border-[#2A3655] overflow-hidden"
                data-testid="reports-view-switcher"
            >
                <button
                    onClick={() => setView("report")}
                    data-testid="reports-view-report"
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                        view === "report"
                            ? "bg-[#007AFF]/20 text-[#00A3FF]"
                            : "text-slate-400 hover:text-white hover:bg-[#1C253C]"
                    }`}
                >
                    <FileText className="w-3 h-3" />
                    Full report
                </button>
                <button
                    onClick={() => setView("quote")}
                    data-testid="reports-view-quote"
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors border-l border-[#2A3655] ${
                        view === "quote"
                            ? "bg-[#007AFF]/20 text-[#00A3FF]"
                            : "text-slate-400 hover:text-white hover:bg-[#1C253C]"
                    }`}
                >
                    <Receipt className="w-3 h-3" />
                    Quote
                </button>
            </div>

            {view === "quote" ? (
                <QuoteView
                    estimate={report}
                    project={project}
                    onSave={onSave}
                    onLocalUpdate={onLocalUpdate}
                />
            ) : (
                <>
                    {/* Totals strip */}
                    <div className="mb-6 grid grid-cols-3 gap-2">
                        <TotalTile label="Subtotal" value={report.totals?.subtotal} />
                        <TotalTile label="Add profit" value={report.totals?.markup_total} />
                        <TotalTile label="Grand total" value={report.totals?.grand_total} emphasis testId="report-grand-total" />
                    </div>

                    {/* Priced lines (merged from old Priced lines tab) */}
                    <Section title="Priced lines">
                        <LineItemsEditor
                            lines={report.lines || []}
                            projectId={report.project_id}
                            onLocalChange={(lines) => onLocalUpdate({ lines })}
                            onCommit={(lines) => onSave({ lines })}
                        />
                    </Section>

                    {/* Full-report markdown body — only when present */}
                    {hasReportMd && (
                        <Section title="Full report (markdown)">
                            <MarkdownEditor
                                value={report.report || ""}
                                onSave={(v) => onSave({ report: v })}
                            />
                        </Section>
                    )}

                    {/* iter-98: "Missing from template" section removed
                        per user request — was cluttering the report. */}

                    {/* iter-98: Open questions now supports inline
                        Answer fields + Revise Quote button that round-
                        trips the answers to the AI estimator. */}
                    <Section title="Open questions">
                        <QuestionsAnswerEditor
                            questions={report.questions || []}
                            estimateId={report.id}
                            onSaveQuestions={(items) => onSave({ questions: items })}
                            revising={revising}
                            reviseStage={reviseStage}
                            onRevise={async (answeredPairs) => {
                                setRevising(true);
                                setReviseStage(null);
                                try {
                                    const newEstimate = await reviseEstimate(
                                        report.id,
                                        answeredPairs,
                                        (stage) => setReviseStage(stage),
                                    );
                                    toast.success(
                                        "Quote revised — opening the new version",
                                    );
                                    // Per user choice B: surface the
                                    // brand-new estimate to the user.
                                    onRevised?.(newEstimate);
                                } catch (err) {
                                    toast.error(
                                        err?.response?.data?.detail ||
                                            err?.message ||
                                            "Couldn't revise the quote",
                                    );
                                } finally {
                                    setRevising(false);
                                    setReviseStage(null);
                                }
                            }}
                        />
                    </Section>
                </>
            )}
        </div>
    );
}

function TotalTile({ label, value, emphasis, testId }) {
    return (
        <div
            className={`rounded-sm border border-[#2A3655] p-3 ${emphasis ? "bg-[#007AFF]/10" : "bg-[#0F1B3A]/60"}`}
            data-testid={testId}
        >
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                {label}
            </div>
            <div
                className={`tabular-nums ${emphasis ? "text-2xl font-bold text-white" : "text-lg text-slate-100"}`}
            >
                ${fmt(value)}
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="mb-6">
            <h2 className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#00A3FF] mb-2">
                {title}
            </h2>
            {children}
        </section>
    );
}

// -------------- Line items editor --------------

function LineItemsEditor({ lines, projectId, onLocalChange, onCommit }) {
    const [local, setLocal] = useState(lines);
    // iter-98: show hidden (soft-deleted) lines toggle. Hidden lines are
    // tucked behind a small "X hidden lines — show" pill; clicking
    // surfaces them grey/struck-through with a Restore icon next to
    // each, matching the QuoteView UX.
    const [showHidden, setShowHidden] = useState(false);
    useEffect(() => setLocal(lines), [lines]);

    const pushLocal = (next) => {
        setLocal(next);
        onLocalChange(next);
    };

    const updateLine = (idx, patch) => {
        const next = local.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln));
        pushLocal(next);
    };

    const commitAll = () => onCommit(local);

    // iter-98: SOFT-delete — flip the line's hidden flag and persist.
    // Subtotals/totals automatically exclude hidden lines because the
    // estimator's totals computation already filters them out.
    const toggleHideLine = (idx) => {
        const next = local.map((ln, i) =>
            i === idx ? { ...ln, hidden: !ln.hidden } : ln,
        );
        pushLocal(next);
        onCommit(next);
    };

    const restoreAllHidden = () => {
        if (!local.some((l) => l.hidden)) return;
        const next = local.map((l) => ({ ...l, hidden: false }));
        pushLocal(next);
        onCommit(next);
        toast.success("All hidden lines restored");
    };

    const hiddenCount = local.filter((l) => l.hidden).length;

    const addLine = () => {
        const next = [
            ...local,
            {
                item_id: "",
                category: "",
                name: "New item",
                description: "",
                quantity: 1,
                unit: "each",
                unit_price: 0,
                price_type: "per_item",
                markup_type: "percent",
                markup_value: 0,
                subtotal: 0,
                markup_amount: 0,
                line_total: 0,
                notes: "",
            },
        ];
        pushLocal(next);
        onCommit(next);
    };

    if (local.length === 0) {
        return (
            <div className="text-center py-6 border border-dashed border-[#2A3655] rounded-sm">
                <p className="text-sm text-slate-400 mb-3">No priced lines yet.</p>
                <button
                    onClick={addLine}
                    data-testid="report-add-line"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white text-xs font-bold uppercase tracking-wider"
                >
                    <Plus className="w-3.5 h-3.5" /> Add line
                </button>
            </div>
        );
    }

    return (
        <div>
            {hiddenCount > 0 && (
                <div
                    className="mb-2 flex items-center justify-between gap-3 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-sm text-xs"
                    data-testid="report-hidden-banner"
                >
                    <button
                        type="button"
                        onClick={() => setShowHidden((s) => !s)}
                        data-testid="report-hidden-toggle"
                        className="flex items-center gap-1.5 text-amber-200 hover:text-amber-100 font-semibold"
                    >
                        <RotateCcw className="w-3.5 h-3.5" />
                        {hiddenCount} hidden line{hiddenCount === 1 ? "" : "s"}
                        <span className="text-amber-300/70 ml-1">
                            — {showHidden ? "hide" : "show"}
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={restoreAllHidden}
                        data-testid="report-restore-all-hidden"
                        className="text-amber-200 hover:text-amber-100 font-bold uppercase tracking-wider text-[10px] border border-amber-500/40 px-2 py-1 rounded-sm hover:bg-amber-500/20"
                    >
                        Restore all
                    </button>
                </div>
            )}
            <div className="rounded-sm border border-[#2A3655] overflow-hidden">
                <div className="grid grid-cols-12 gap-1 px-2 py-2 bg-[#0A1128] border-b border-[#2A3655] text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    <div className="col-span-3">Item</div>
                    <div className="col-span-1 text-right">Qty</div>
                    <div className="col-span-1">Unit</div>
                    <div className="col-span-2 text-right">Unit price</div>
                    <div className="col-span-1">Type</div>
                    <div className="col-span-2">Add profit</div>
                    <div className="col-span-1 text-right">Total</div>
                    <div className="col-span-1" />
                </div>
                {local.map((ln, i) => {
                    if (ln.hidden && !showHidden) return null;
                    return (
                        <LineRow
                            key={i}
                            line={ln}
                            idx={i}
                            projectId={projectId}
                            onChange={(patch) => updateLine(i, patch)}
                            onBlur={commitAll}
                            onToggleHide={() => toggleHideLine(i)}
                        />
                    );
                })}
            </div>
            <button
                onClick={addLine}
                data-testid="report-add-line"
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#1C253C] hover:bg-[#223055] border border-dashed border-[#2A3655] hover:border-[#00A3FF] text-xs font-semibold text-slate-200 hover:text-white transition-colors"
            >
                <Plus className="w-3.5 h-3.5 text-[#00A3FF]" /> Add line
            </button>
        </div>
    );
}

function LineRow({ line, idx, projectId, onChange, onBlur, onToggleHide }) {
    const navigate = useNavigate();
    const F = "w-full bg-[#0A1128] border border-[#2A3655] rounded-sm px-1.5 py-1 text-[11px] text-white focus:outline-none focus:border-[#00A3FF]";
    const takeoffIds = Array.isArray(line.takeoff_item_ids) ? line.takeoff_item_ids.filter(Boolean) : [];
    const jumpToTakeoff = () => {
        if (!projectId || takeoffIds.length === 0) return;
        navigate(`/workspace/${projectId}?takeoff=${encodeURIComponent(takeoffIds[0])}`);
    };
    return (
        <div
            className={`grid grid-cols-12 gap-1 px-2 py-2 border-b border-[#1C253C] last:border-0 hover:bg-[#0F1B3A]/60 items-start ${line.hidden ? "opacity-40 line-through" : ""}`}
            data-testid={`report-line-${idx}`}
        >
            <div className="col-span-3 space-y-1">
                <input
                    className={F}
                    value={line.name || ""}
                    onChange={(e) => onChange({ name: e.target.value })}
                    onBlur={onBlur}
                    placeholder="Name"
                    data-testid={`report-line-${idx}-name`}
                />
                <input
                    className={F + " text-[10px] text-slate-400"}
                    value={line.category || ""}
                    onChange={(e) => onChange({ category: e.target.value })}
                    onBlur={onBlur}
                    placeholder="Category"
                />
                <input
                    className={F + " text-[10px] text-slate-400 italic"}
                    value={line.notes || ""}
                    onChange={(e) => onChange({ notes: e.target.value })}
                    onBlur={onBlur}
                    placeholder="Notes / assumption"
                />
                {takeoffIds.length > 0 && (
                    <button
                        type="button"
                        onClick={jumpToTakeoff}
                        data-testid={`report-line-${idx}-measured-pill`}
                        title={`Jump to the ${takeoffIds.length === 1 ? "measured shape" : `${takeoffIds.length} measured shapes`} on the blueprint`}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#00C853]/15 border border-[#00C853]/50 text-[9px] uppercase tracking-wider font-bold text-[#00E676] hover:bg-[#00C853]/25 hover:border-[#00C853] transition-colors"
                    >
                        <Ruler className="w-2.5 h-2.5" />
                        Measured{takeoffIds.length > 1 ? ` · ${takeoffIds.length}` : ""}
                    </button>
                )}
            </div>
            <input
                type="number"
                step="0.01"
                min="0"
                className={F + " col-span-1 text-right tabular-nums"}
                value={line.quantity ?? 0}
                onChange={(e) => onChange({ quantity: e.target.value })}
                onBlur={onBlur}
                data-testid={`report-line-${idx}-qty`}
            />
            <select
                className={F + " col-span-1"}
                value={line.unit || ""}
                onChange={(e) => {
                    onChange({ unit: e.target.value });
                    onBlur();
                }}
            >
                {UNITS.map((u) => (
                    <option key={u} value={u}>{u || "—"}</option>
                ))}
            </select>
            <div className="col-span-2 flex items-center gap-0.5">
                <span className="text-slate-500 text-[11px]">$</span>
                <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={F + " flex-1 text-right tabular-nums"}
                    value={line.unit_price ?? 0}
                    onChange={(e) => onChange({ unit_price: e.target.value })}
                    onBlur={onBlur}
                    data-testid={`report-line-${idx}-price`}
                />
            </div>
            <select
                className={F + " col-span-1"}
                value={line.price_type || "fixed"}
                onChange={(e) => {
                    onChange({ price_type: e.target.value });
                    onBlur();
                }}
            >
                {PRICE_TYPES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                ))}
            </select>
            <div className="col-span-2 flex items-center gap-0.5">
                <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={F + " flex-1 text-right tabular-nums"}
                    value={line.markup_value ?? 0}
                    onChange={(e) => onChange({ markup_value: e.target.value })}
                    onBlur={onBlur}
                    data-testid={`report-line-${idx}-markup`}
                />
                <select
                    className={F + " w-16"}
                    value={line.markup_type || "percent"}
                    onChange={(e) => {
                        onChange({ markup_type: e.target.value });
                        onBlur();
                    }}
                >
                    <option value="percent">%</option>
                    <option value="flat">$</option>
                </select>
            </div>
            <div className="col-span-1 text-right tabular-nums text-sm font-semibold text-white pt-1">
                ${fmt(line.line_total)}
            </div>
            <div className="col-span-1 flex items-center justify-end">
                <button
                    onClick={onToggleHide}
                    data-testid={`report-line-${idx}-${line.hidden ? "restore" : "delete"}`}
                    className={`p-1.5 rounded-sm transition-colors no-underline ${
                        line.hidden
                            ? "text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            : "text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                    }`}
                    title={line.hidden ? "Restore line" : "Remove (stays saved, can be restored)"}
                    aria-label={line.hidden ? "Restore line" : "Hide line"}
                >
                    {line.hidden ? (
                        <RotateCcw className="w-3.5 h-3.5" />
                    ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                    )}
                </button>
            </div>
        </div>
    );
}

// -------------- Markdown editor --------------

function MarkdownEditor({ value, onSave }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || "");
    useEffect(() => setDraft(value || ""), [value]);

    if (!editing) {
        return (
            <div className="rounded-sm border border-[#2A3655] bg-[#0F1B3A]/40 overflow-hidden">
                <div className="flex items-center justify-end p-1.5 border-b border-[#2A3655] bg-[#0A1128]">
                    <button
                        onClick={() => setEditing(true)}
                        data-testid="report-markdown-edit-btn"
                        className="inline-flex items-center gap-1 text-xs text-[#00A3FF] hover:bg-[#00A3FF]/10 px-2 py-1 rounded-sm"
                    >
                        <Edit3 className="w-3 h-3" /> Edit
                    </button>
                </div>
                <div
                    className="prose prose-invert prose-sm max-w-none p-4"
                    data-testid="report-markdown-view"
                >
                    {value ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                            {value}
                        </ReactMarkdown>
                    ) : (
                        <p className="text-slate-500 italic text-sm">No markdown report saved.</p>
                    )}
                </div>
            </div>
        );
    }
    return (
        <div className="rounded-sm border border-[#00A3FF]/50 bg-[#0F1B3A]/60 overflow-hidden">
            <div className="flex items-center justify-end gap-2 p-1.5 border-b border-[#2A3655] bg-[#0A1128]">
                <button
                    onClick={() => {
                        setDraft(value || "");
                        setEditing(false);
                    }}
                    className="text-xs text-slate-300 hover:text-white px-2 py-1 rounded-sm"
                    data-testid="report-markdown-cancel"
                >
                    Cancel
                </button>
                <button
                    onClick={async () => {
                        await onSave(draft);
                        setEditing(false);
                    }}
                    data-testid="report-markdown-save"
                    className="inline-flex items-center gap-1 text-xs bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold px-3 py-1 rounded-sm"
                >
                    <Check className="w-3 h-3" /> Save
                </button>
            </div>
            <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="w-full min-h-[60vh] p-4 bg-transparent text-sm font-mono text-slate-100 focus:outline-none"
                data-testid="report-markdown-editor"
            />
        </div>
    );
}

// -------------- String list editor --------------

function StringListEditor({ items, onSave, placeholder, testIdRoot }) {
    const [local, setLocal] = useState(items);
    const [draft, setDraft] = useState("");
    useEffect(() => setLocal(items), [items]);

    const commit = (next) => {
        setLocal(next);
        onSave(next);
    };

    return (
        <div>
            <ul className="space-y-1 mb-2">
                {local.length === 0 && (
                    <li className="text-xs text-slate-500 italic">None.</li>
                )}
                {local.map((s, i) => (
                    <li
                        key={i}
                        className="flex items-start gap-2"
                        data-testid={`report-${testIdRoot}-${i}`}
                    >
                        <span className="text-[#00A3FF] text-xs mt-1.5">•</span>
                        <input
                            className="flex-1 bg-[#0A1128] border border-[#2A3655] rounded-sm px-2 py-1 text-sm text-white focus:outline-none focus:border-[#00A3FF]"
                            value={s}
                            onChange={(e) => {
                                const next = local.map((x, j) => (j === i ? e.target.value : x));
                                setLocal(next);
                            }}
                            onBlur={() => commit(local)}
                        />
                        <button
                            onClick={() => commit(local.filter((_, j) => j !== i))}
                            className="p-1.5 rounded-sm text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            data-testid={`report-${testIdRoot}-${i}-delete`}
                            aria-label="Delete"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </li>
                ))}
            </ul>
            <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                    e.preventDefault();
                    const v = draft.trim();
                    if (!v) return;
                    commit([...local, v]);
                    setDraft("");
                }}
            >
                <input
                    className="flex-1 bg-[#0A1128] border border-[#2A3655] rounded-sm px-2 py-1 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00A3FF]"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={placeholder}
                    data-testid={`report-${testIdRoot}-new-input`}
                />
                <button
                    type="submit"
                    className="inline-flex items-center gap-1 text-xs bg-[#1C253C] hover:bg-[#223055] border border-[#2A3655] hover:border-[#00A3FF] text-slate-200 hover:text-white px-3 py-1.5 rounded-sm font-bold"
                    data-testid={`report-${testIdRoot}-add`}
                >
                    <Plus className="w-3 h-3" /> Add
                </button>
            </form>
        </div>
    );
}


// -------------- iter-98: Questions answer + revise editor --------------

/**
 * Combines the old "Open questions" list with an inline Answer affordance
 * per question and a primary "Revise Quote" CTA that ships every answered
 * pair to the AI estimator. Answers are kept in local state until the
 * user clicks Revise — they are NEVER persisted on the source estimate,
 * since the revision creates a brand-new estimate doc and the source
 * stays untouched (user choice B in the iter-98 spec).
 */
function QuestionsAnswerEditor({
    questions,
    estimateId,  // eslint-disable-line no-unused-vars
    onSaveQuestions,
    revising,
    reviseStage,
    onRevise,
}) {
    const [local, setLocal] = useState(questions);
    const [answers, setAnswers] = useState({}); // {idx: "answer text"}
    const [openAnswerIdx, setOpenAnswerIdx] = useState(null);
    const [newQ, setNewQ] = useState("");
    useEffect(() => {
        setLocal(questions);
        // When the question list shifts (e.g. after a successful
        // revise loads the new estimate), purge stale answer drafts.
        setAnswers({});
        setOpenAnswerIdx(null);
    }, [questions]);

    const commitQuestions = (next) => {
        setLocal(next);
        onSaveQuestions(next);
    };

    const answeredCount = Object.values(answers).filter(
        (a) => (a || "").trim().length > 0,
    ).length;

    const handleRevise = () => {
        const pairs = local
            .map((q, i) => ({ question: q, answer: (answers[i] || "").trim() }))
            .filter((p) => p.question.trim() && p.answer);
        if (pairs.length === 0) {
            toast.error("Answer at least one question before revising.");
            return;
        }
        onRevise?.(pairs);
    };

    return (
        <div>
            <ul className="space-y-2 mb-3">
                {local.length === 0 && (
                    <li className="text-xs text-slate-500 italic">
                        No open questions — every assumption is locked in.
                    </li>
                )}
                {local.map((q, i) => {
                    const isOpen = openAnswerIdx === i;
                    const ans = answers[i] || "";
                    const hasAnswer = ans.trim().length > 0;
                    return (
                        <li
                            key={i}
                            className="rounded-sm border border-[#2A3655] bg-[#0F1B3A]/40 overflow-hidden"
                            data-testid={`report-question-${i}`}
                        >
                            <div className="flex items-start gap-2 p-2">
                                <span className="text-[#00A3FF] text-xs mt-1.5">•</span>
                                <input
                                    className="flex-1 bg-[#0A1128] border border-[#2A3655] rounded-sm px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#00A3FF]"
                                    value={q}
                                    onChange={(e) => {
                                        const next = local.map((x, j) =>
                                            j === i ? e.target.value : x,
                                        );
                                        setLocal(next);
                                    }}
                                    onBlur={() => commitQuestions(local)}
                                    data-testid={`report-question-${i}-text`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setOpenAnswerIdx(isOpen ? null : i)}
                                    disabled={revising}
                                    data-testid={`report-question-${i}-answer-btn`}
                                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm border transition-colors disabled:opacity-50 ${
                                        hasAnswer
                                            ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                                            : isOpen
                                                ? "border-[#00A3FF] bg-[#00A3FF]/15 text-[#00A3FF]"
                                                : "border-[#2A3655] bg-[#1C253C] text-slate-300 hover:border-[#00A3FF] hover:text-[#00A3FF]"
                                    }`}
                                >
                                    <MessageSquare className="w-3 h-3" />
                                    {hasAnswer ? "Answered" : isOpen ? "Close" : "Answer"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() =>
                                        commitQuestions(
                                            local.filter((_, j) => j !== i),
                                        )
                                    }
                                    disabled={revising}
                                    className="p-1.5 rounded-sm text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                                    data-testid={`report-question-${i}-delete`}
                                    aria-label="Delete question"
                                    title="Remove this question"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            {isOpen && (
                                <div className="px-2 pb-2" data-testid={`report-question-${i}-answer-box`}>
                                    <textarea
                                        autoFocus
                                        rows={3}
                                        placeholder="Type your answer — be specific. The AI will use this to revise the quote."
                                        className="w-full bg-[#0A1128] border border-[#00A3FF]/40 rounded-sm px-2 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00A3FF]"
                                        value={ans}
                                        onChange={(e) =>
                                            setAnswers((a) => ({ ...a, [i]: e.target.value }))
                                        }
                                        data-testid={`report-question-${i}-answer-input`}
                                    />
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>

            <form
                className="flex items-center gap-2 mb-4"
                onSubmit={(e) => {
                    e.preventDefault();
                    const v = newQ.trim();
                    if (!v) return;
                    commitQuestions([...local, v]);
                    setNewQ("");
                }}
            >
                <input
                    className="flex-1 bg-[#0A1128] border border-[#2A3655] rounded-sm px-2 py-1 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00A3FF]"
                    value={newQ}
                    onChange={(e) => setNewQ(e.target.value)}
                    placeholder="Add a question…"
                    data-testid="report-question-new-input"
                />
                <button
                    type="submit"
                    className="inline-flex items-center gap-1 text-xs bg-[#1C253C] hover:bg-[#223055] border border-[#2A3655] hover:border-[#00A3FF] text-slate-200 hover:text-white px-3 py-1.5 rounded-sm font-bold"
                    data-testid="report-question-add"
                >
                    <Plus className="w-3 h-3" /> Add
                </button>
            </form>

            {/* Revise quote CTA — only meaningful once at least one
                question has been answered. */}
            <div className="border-t border-[#2A3655] pt-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs text-slate-400">
                        {answeredCount > 0 ? (
                            <>
                                <span className="text-emerald-300 font-bold">
                                    {answeredCount} answer{answeredCount === 1 ? "" : "s"}
                                </span>{" "}
                                ready — revise to update the quote with your input.
                            </>
                        ) : (
                            <>Answer at least one question to enable Revise Quote.</>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleRevise}
                        disabled={revising || answeredCount === 0}
                        data-testid="report-revise-quote-btn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-[#00A3FF] hover:bg-[#3395FF] disabled:bg-[#1C253C] disabled:text-slate-500 text-white font-bold uppercase tracking-wider text-xs transition-colors disabled:cursor-not-allowed"
                    >
                        {revising ? (
                            <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                {reviseStage?.stage || "Revising…"}
                            </>
                        ) : (
                            <>
                                <Sparkles className="w-3.5 h-3.5" />
                                Revise quote
                            </>
                        )}
                    </button>
                </div>
                {revising && reviseStage?.progress_pct != null && (
                    <div className="mt-2 h-1 bg-[#0A1128] rounded-sm overflow-hidden">
                        <div
                            className="h-full bg-[#00A3FF] transition-all"
                            style={{ width: `${reviseStage.progress_pct}%` }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
